import { useSyncExternalStore } from 'react';
import { levelRank } from '@shared/levels';
import type { RotationReviewItem } from '@shared/views';

/**
 * ร่างรอบโยกย้าย — ตะกร้าที่ผู้ใช้หยิบคนใส่ก่อนออกเป็นบันทึกเสนอ
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องมีตะกร้า ไม่ใช่กดดูทีละคนแล้วจบ
 * ────────────────────────────────────────────────────────────────
 *
 * คำสั่งโยกย้ายจริงออกเป็น "ชุด" ไม่ใช่ทีละคน หน้าจอที่ให้ดูได้อย่างเดียว
 * บังคับให้ผู้ใช้จดใส่กระดาษข้าง ๆ จอ แล้วมานั่งบวกเองว่าตกลงปิดช่องว่างไปกี่ช่อง
 * ซึ่งเป็นเลขที่บวกผิดง่ายที่สุดเพราะการย้ายหนึ่งครั้งเปิดช่องว่างใหม่เสมอ
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ เก็บใน localStorage ไม่ใช่ที่เซิร์ฟเวอร์ — และรู้ตัวว่าแลกอะไรไป
 * ────────────────────────────────────────────────────────────────
 *
 * ระบบนี้ยังไม่มีฐานข้อมูล อ่านจากไฟล์ JSON อย่างเดียว ร่างเป็นข้อมูลชิ้นแรก
 * ที่ "ผู้ใช้สร้างขึ้น" จึงไม่มีที่ให้เขียนลง
 *
 * ผลที่ตามมาซึ่งต้องบอกผู้ใช้บนหน้าจอ ห้ามซ่อน
 *   - อยู่กับเบราว์เซอร์เครื่องนั้น เปลี่ยนเครื่องแล้วไม่เจอ
 *   - ล้างข้อมูลเบราว์เซอร์แล้วหาย
 *   - ส่งให้คนอื่นดูไม่ได้ ต้องออกเป็นไฟล์ก่อน
 *
 * วันที่ต้องทำงานเป็นทีม (กองการเจ้าหน้าที่ร่าง แล้วเสนอปลัด) ต้องย้ายไปเซิร์ฟเวอร์
 * ทุกอย่างที่แตะ localStorage อยู่ในไฟล์นี้ไฟล์เดียว — เปลี่ยน read/write สองฟังก์ชัน
 * ข้างล่างเป็นการเรียก API แล้วจบ ไม่ต้องแตะหน้าจอเลย
 */

export interface DraftPerson {
  personaId: string | null;
  name: string;
  /** เลขที่ตำแหน่งเดิม — ตัวที่จะกลายเป็นอัตราว่างหลังย้าย ใช้ต่อลูกโซ่ */
  posNo: string | null;
  title: string;
  unit: string;
  level: string;
  monthsInPosition: number | null;
  retireInYears: number | null;
}

export interface DraftTarget {
  posNo: string | null;
  title: string;
  unit: string;
  level: string;
  line: string;
  vacantMonths: number | null;
}

/** ข้อย่อยของ กฎ ก.พ. 2564 ข้อ 3 — หกอย่างที่กฎกำหนดให้คำนึงถึง */
export type ClauseId = 1 | 2 | 3 | 4 | 5 | 6;

export interface DraftReason {
  clauses: ClauseId[];
  text: string;
  /** หลักฐานที่ระบบแนบให้เอง — null เมื่อระบบหาให้ไม่ได้ */
  evidence: string | null;
}

/**
 * สถานะของแถวในร่าง
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ทำไมต้องมีสองสถานะ ไม่ใช่ "อยู่ในร่าง" อย่างเดียว
 * ────────────────────────────────────────────────────────────────
 *
 * งานจริงคือตั้งบัญชีผู้ได้รับการพิจารณาหลายคนต่อหนึ่งอัตราก่อน แล้วค่อยเลือก
 * ถ้ามีสถานะเดียว จะเหลือทางเลือกแค่สองอย่างซึ่งผิดทั้งคู่ —
 * ห้ามใส่หลายคน (ทำงานจริงไม่ได้) หรือปล่อยให้หลายคนนับเข้ายอด (ตัวเลขผิดหมด)
 *
 *   considered  อยู่ในบัญชีผู้ได้รับการพิจารณา — **ไม่นับเข้ายอดใด ๆ ทั้งสิ้น**
 *   proposed    ผู้ที่เสนอจริง — อัตราละไม่เกินหนึ่งคน และคนละไม่เกินหนึ่งอัตรา
 *
 * ⚠ ทุกฟังก์ชันที่คำนวณตัวเลข (summarise, draftIssues, toReviewItems, draftExport)
 *   ต้องกรองเอาเฉพาะ proposed ก่อนเสมอ และกรองข้างในตัวเองไม่ใช่ให้ผู้เรียกกรองมา
 *   ผู้เรียกลืมกรองเมื่อไร ตัวเลขบนหน้าจอผิดทันทีโดยไม่มีอะไรฟ้อง
 */
export type DraftStatus = 'considered' | 'proposed';

export interface DraftItem {
  id: string;
  person: DraftPerson;
  target: DraftTarget;
  /** ⚠ ร่างเก่าที่ค้างใน localStorage ไม่มีช่องนี้ — load() เติมให้เป็น proposed */
  status: DraftStatus;
  /** null = ยังไม่ได้ระบุ ซึ่งไม่ใช่ความผิด แต่ต้องนับและต้องพิมพ์ลงเอกสาร */
  reason: DraftReason | null;
  /** ติ๊กเมื่อแนบหนังสือยินยอมแล้ว — ใช้เฉพาะกรณีย้ายไประดับต่ำกว่าเดิม */
  consentAttached: boolean;
  addedAt: string;
}

/**
 * หกข้อตาม กฎ ก.พ. ว่าด้วยการย้าย การโอน หรือการเลื่อน พ.ศ. 2564 ข้อ 3
 *
 * ⚠ ช่อง hasData ต้องตรงกับความจริงของชุดข้อมูลเสมอ ห้ามตั้งเป็น true ไว้ก่อน
 *   ข้อ 4 เป็นข้อเดียวที่ระบบมีของจริง (work_history) อีกห้าข้ออยู่ในไฟล์ dump ก็จริง
 *   แต่ enhancedMeta.generatedFields ระบุว่าเป็นค่าที่สร้างขึ้น จึงถือว่าไม่มี
 *   ถ้าวันหนึ่ง DPIS ส่งของจริงมา ให้แก้ที่นี่ที่เดียว
 */
export const TRANSFER_CLAUSES: ReadonlyArray<{ id: ClauseId; label: string; hasData: boolean }> = [
  { id: 1, label: 'ศักยภาพ', hasData: false },
  { id: 2, label: 'ผลสัมฤทธิ์ของงาน', hasData: false },
  { id: 3, label: 'สมรรถนะ', hasData: false },
  { id: 4, label: 'ประวัติการรับราชการ', hasData: true },
  { id: 5, label: 'ความรู้ ความสามารถและความเชี่ยวชาญ', hasData: false },
  { id: 6, label: 'พฤติกรรมทางจริยธรรมและความประพฤติ', hasData: false },
];

export function clauseLabel(id: ClauseId): string {
  return TRANSFER_CLAUSES.find((c) => c.id === id)?.label ?? `ข้อ 3 (${id})`;
}

/** เหตุผลสำเร็จรูปที่เจอบ่อย — ปุ่มเดียวจบ ดีกว่าปล่อยให้พิมพ์ "." ให้ผ่านช่อง */
export const QUICK_REASONS: ReadonlyArray<{ text: string; clause: ClauseId }> = [
  { text: 'เป็นผู้อยู่ในข่ายรายเดียวของอัตรานี้', clause: 4 },
  { text: 'ครองตำแหน่งเดิมมานาน ถึงกำหนดหมุนเวียน', clause: 4 },
  { text: 'หน่วยงานปลายทางร้องขอ', clause: 2 },
  { text: 'เป็นไปตามความสมัครใจของเจ้าตัว', clause: 1 },
];

/* ================================================================
   ที่เก็บ
   ================================================================ */

const KEY = 'yana.rotation.draft.v1';

let items: DraftItem[] = load();
const listeners = new Set<() => void>();

/** ⚠ ทุกการอ่านต้องกันพัง — โหมดส่วนตัวของบางเบราว์เซอร์โยน error ตั้งแต่ตอนอ่าน */
function load(): DraftItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    /* ⚠ ร่างที่บันทึกไว้ก่อนมีสถานะ ถือเป็น "เสนอ" ทั้งหมด
       ถ้าเติมเป็น considered ยอดของผู้ใช้จะกลายเป็นศูนย์เงียบ ๆ ตอนอัปเดตเวอร์ชัน */
    return (parsed as DraftItem[]).map((it) => ({ ...it, status: it.status ?? 'proposed' }));
  } catch {
    return [];
  }
}

function save(next: DraftItem[]): void {
  items = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* เขียนไม่ได้ก็ยังใช้งานต่อในหน้านี้ได้ แค่ไม่ค้างข้ามการรีเฟรช */
  }
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function snapshot(): DraftItem[] {
  return items;
}

export function useDraft(): DraftItem[] {
  return useSyncExternalStore(subscribe, snapshot, () => [] as DraftItem[]);
}

/** กุญแจประจำแถว — คนหนึ่งคนต่ออัตราปลายทางหนึ่งอัตรา */
export function itemKey(personaId: string | null, name: string, targetPosNo: string | null): string {
  return `${personaId ?? name}→${targetPosNo ?? '?'}`;
}

export function inDraft(list: DraftItem[], personaId: string | null, name: string, targetPosNo: string | null): DraftItem | undefined {
  const k = itemKey(personaId, name, targetPosNo);
  return list.find((it) => it.id === k);
}

/**
 * คนคนนี้อยู่ในร่างแล้วหรือยัง ไม่ว่าจะไปลงอัตราไหน
 *
 * ⚠ คนหนึ่งคนอยู่ในร่างได้อัตราเดียว
 *   คำสั่งแต่งตั้งคนเดียวไปสองตำแหน่งพร้อมกันเป็นไปไม่ได้ และถ้าปล่อยให้หยิบซ้ำ
 *   ตัวเลข "เปิดช่องว่างใหม่" จะนับอัตราเดิมของเขาสองครั้ง ทั้งที่มีอยู่ที่เดียว
 *   ซึ่งทำให้เลขสุทธิผิดโดยไม่มีอะไรบนหน้าจอฟ้อง
 */
export function personInDraft(list: DraftItem[], personaId: string | null, name: string): DraftItem | undefined {
  return list.find((it) => samePerson(it, personaId, name));
}

function samePerson(it: DraftItem, personaId: string | null, name: string): boolean {
  return personaId ? it.person.personaId === personaId : it.person.name === name && !it.person.personaId;
}

/** คนคนนี้ถูก **เสนอ** ไว้ที่อัตราไหนแล้วหรือยัง — อยู่ในบัญชีพิจารณาหลายที่ได้ แต่เสนอได้ที่เดียว */
export function personProposed(list: DraftItem[], personaId: string | null, name: string): DraftItem | undefined {
  return list.find((it) => it.status === 'proposed' && samePerson(it, personaId, name));
}

/** บัญชีผู้ได้รับการพิจารณาของอัตราหนึ่ง — เรียงตามลำดับที่ใส่ */
export function shortlistOf(list: DraftItem[], targetPosNo: string | null): DraftItem[] {
  if (!targetPosNo) return [];
  return list.filter((it) => it.target.posNo === targetPosNo);
}

/**
 * อัตราปลายทางนี้มีคนถูกเสนอไว้แล้วหรือยัง
 *
 * ⚠ ด้านกลับของ personInDraft และสำคัญเท่ากัน — อัตราหนึ่งอัตรารับได้คนเดียว
 *   คำสั่งแต่งตั้งคนสามคนไปนั่งเก้าอี้ตัวเดียวกันเป็นไปไม่ได้
 *
 *   ถ้าปล่อยให้ซ้ำ ตัวเลขทุกตัวบนหน้าจอผิดหมด — "ปิดอัตราว่าง" นับเก้าอี้ตัวเดียวสามครั้ง
 *   หน่วยปลายทางขึ้นว่า +3 ทั้งที่มีที่นั่งเดียว และเลขสุทธิบอกว่าเท่าเดิม
 *   ทั้งที่ของจริงอัตราว่างเพิ่มขึ้นสองอัตรา (เจอจากผู้ใช้ทัก 10 ก.ย. 2569)
 *
 * ⚠ ถ้าวันหนึ่งอยากเก็บ "ตัวเลือกสำรอง" ของอัตราเดียวกัน อย่าทำด้วยการปล่อยให้ซ้ำในร่าง
 *   ต้องเป็นสถานะแยกที่ไม่ถูกนับเข้ายอด ไม่งั้นจะกลับไปเป็นบั๊กเดิม
 */
export function targetInDraft(list: DraftItem[], targetPosNo: string | null): DraftItem | undefined {
  if (!targetPosNo) return undefined;
  return list.find((it) => it.status === 'proposed' && it.target.posNo === targetPosNo);
}

/**
 * ใส่คนเข้าบัญชีผู้ได้รับการพิจารณาของอัตราหนึ่ง
 *
 * สถานะตั้งต้นคือ considered เสมอ — ไม่นับเข้ายอด ไม่ต้องกัน ใส่กี่คนก็ได้
 * การเลือกจริงเกิดตอนกด propose() ซึ่งเป็นที่เดียวที่มีข้อห้าม
 */
export function addToDraft(person: DraftPerson, target: DraftTarget): string | null {
  const id = itemKey(person.personaId, person.name, target.posNo);
  if (items.some((it) => it.id === id)) return id;
  save([
    ...items,
    {
      id,
      person,
      target,
      status: 'considered',
      reason: null,
      consentAttached: false,
      addedAt: new Date().toISOString(),
    },
  ]);
  return id;
}

/**
 * ติดธง "เสนอ" ให้แถวนี้
 *
 * ⚠ สองข้อห้ามที่บังคับตรงนี้ที่เดียว — ที่อื่นในโปรแกรมไม่ต้องรู้เรื่องนี้เลย
 *   1 อัตราหนึ่งอัตราเสนอได้คนเดียว — คนที่เคยติดธงของอัตราเดียวกันถูกลดกลับเป็น considered
 *     (ไม่ใช่ปฏิเสธ เพราะการเปลี่ยนตัวในอัตราเดียวกันคือสิ่งที่ผู้ใช้ตั้งใจทำ)
 *   2 คนหนึ่งคนถูกเสนอได้อัตราเดียว — กรณีนี้ **ปฏิเสธ** พร้อมคืนแถวที่ชนกัน
 *     เพราะการย้ายคนเดียวไปสองตำแหน่งเป็นคนละเรื่องกับการเปลี่ยนตัว ผู้ใช้ต้องตัดสินเอง
 */
export function propose(id: string): { ok: true } | { ok: false; conflict: DraftItem } {
  const me = items.find((it) => it.id === id);
  if (!me) return { ok: true };
  const clash = items.find(
    (it) => it.id !== id && it.status === 'proposed' && samePerson(it, me.person.personaId, me.person.name),
  );
  if (clash) return { ok: false, conflict: clash };
  save(
    items.map((it) => {
      if (it.id === id) return { ...it, status: 'proposed' as const };
      if (it.target.posNo && it.target.posNo === me.target.posNo && it.status === 'proposed') {
        return { ...it, status: 'considered' as const };
      }
      return it;
    }),
  );
  return { ok: true };
}

/** ถอนธง "เสนอ" กลับเป็นผู้ได้รับการพิจารณา — ยังอยู่ในบัญชี ไม่ได้ถูกลบ */
export function unpropose(id: string): void {
  save(items.map((it) => (it.id === id ? { ...it, status: 'considered' } : it)));
}

export function removeFromDraft(id: string): void {
  save(items.filter((it) => it.id !== id));
}

export function setReason(id: string, reason: DraftReason | null): void {
  save(items.map((it) => (it.id === id ? { ...it, reason } : it)));
}

export function setConsent(id: string, on: boolean): void {
  save(items.map((it) => (it.id === id ? { ...it, consentAttached: on } : it)));
}

export function clearDraft(): void {
  save([]);
}

/* ================================================================
   สิ่งที่คำนวณได้จากร่าง — ทั้งหมดเป็นเลขคณิต ไม่มี AI แตะ
   ================================================================ */

export interface DraftSummary {
  /** อัตราว่างที่ถูกปิดด้วยร่างนี้ */
  closed: number;
  /** อัตราว่างใหม่ที่ร่างนี้เปิดขึ้น และยังไม่มีใครมาแทน */
  opened: number;
  net: number;
  openedFrom: DraftItem[];
  unitDelta: Array<{ unit: string; delta: number }>;
}

/**
 * ⚠ กรอง proposed **ข้างในนี้** ไม่ใช่ให้ผู้เรียกกรองมา
 *   ผู้ที่อยู่ในบัญชีพิจารณาแต่ยังไม่ถูกเสนอ ห้ามนับเข้ายอดใด ๆ ทั้งสิ้น
 *   ถ้าย้ายการกรองออกไปอยู่ที่ผู้เรียก วันหนึ่งจะมีคนเรียกโดยลืมกรอง แล้วตัวเลขผิดเงียบ ๆ
 */
export function summarise(all: DraftItem[]): DraftSummary {
  const list = all.filter((it) => it.status === 'proposed');
  /* อัตราเดิมของคนนี้ ถูกปิดต่อด้วยรายการอื่นในร่างเดียวกันหรือยัง */
  const filledPosNos = new Set(list.map((it) => it.target.posNo).filter(Boolean) as string[]);
  const openedFrom = list.filter((it) => !it.person.posNo || !filledPosNos.has(it.person.posNo));

  const delta = new Map<string, number>();
  for (const it of list) {
    delta.set(it.person.unit, (delta.get(it.person.unit) ?? 0) - 1);
    delta.set(it.target.unit, (delta.get(it.target.unit) ?? 0) + 1);
  }

  /* ⚠ นับอัตราปลายทางที่ไม่ซ้ำกัน ไม่ใช่นับจำนวนแถว
     ปกติ addToDraft กันซ้ำไว้แล้ว แต่ร่างเก่าที่ค้างใน localStorage ตั้งแต่ก่อนแก้
     ยังมีแถวซ้ำได้ ถ้านับจากจำนวนแถวตรง ๆ เลขจะผิดโดยไม่มีอะไรฟ้อง */
  const distinctTargets = new Set(list.map((it) => it.target.posNo ?? `?${it.id}`)).size;

  return {
    closed: distinctTargets,
    opened: openedFrom.length,
    net: distinctTargets - openedFrom.length,
    openedFrom,
    unitDelta: [...delta.entries()]
      .map(([unit, d]) => ({ unit, delta: d }))
      .filter((u) => u.delta !== 0)
      .sort((a, b) => a.delta - b.delta),
  };
}

/** ย้ายไปดำรงตำแหน่งในระดับที่ต่ำกว่าเดิมหรือไม่ — ตัวจุดชนวนของ ข้อ 6 */
export function isLevelDown(it: DraftItem): boolean {
  const from = levelRank(it.person.level);
  const to = levelRank(it.target.level);
  /* เลขน้อย = สูงกว่า (ดู LEVEL_ORDER) และ 99 คือระดับที่อ่านไม่ออก ห้ามเอามาเทียบ */
  if (from === 99 || to === 99) return false;
  return to > from;
}

export type IssueTone = 'block' | 'warn' | 'note';

export interface DraftIssue {
  id: string;
  tone: IssueTone;
  title: string;
  detail: string;
  /** ที่มาของข้อสังเกต — ต้องขึ้นหน้าจอเสมอ ผู้อ่านต้องรู้ว่าอะไรเถียงไม่ได้ */
  source: 'ตรวจจากกฎ' | 'คำนวณ';
}

const CONSENT_RULE =
  'กฎ ก.พ. ว่าด้วยการย้าย การโอน หรือการเลื่อนข้าราชการพลเรือนสามัญ พ.ศ. 2564 ข้อ 6 ' +
  'กำหนดว่าการย้ายหรือโอนไปดำรงตำแหน่งในระดับที่ต่ำกว่าเดิม จะกระทำได้ต่อเมื่อได้รับ' +
  'ความยินยอมจากผู้ถูกย้ายหรือโอนนั้น';

/**
 * ข้อควรแก้ก่อนเสนอ — คำนวณล้วน ๆ ไม่ต้องเรียกโมเดล
 *
 * ⚠ ทุกข้อในนี้ต้องอธิบายได้ว่ามาจากไหน ห้ามมีข้อไหนที่เป็นความเห็น
 *   ส่วนที่เป็นความเห็นจริง ๆ (เช่น ถ้อยคำของเหตุผลสอดคล้องกันไหม)
 *   เป็นหน้าที่ของผู้ช่วย ไม่ใช่ของฟังก์ชันนี้
 */
/**
 * ⚠ กรอง proposed **ข้างในนี้** ไม่ใช่ให้ผู้เรียกกรองมา
 *   ผู้ที่อยู่ในบัญชีพิจารณาแต่ยังไม่ถูกเสนอ ห้ามนับเข้ายอดใด ๆ ทั้งสิ้น
 *   ถ้าย้ายการกรองออกไปอยู่ที่ผู้เรียก วันหนึ่งจะมีคนเรียกโดยลืมกรอง แล้วตัวเลขผิดเงียบ ๆ
 */
export function draftIssues(all: DraftItem[], tenureMonths: number): DraftIssue[] {
  const list = all.filter((it) => it.status === 'proposed');
  const out: DraftIssue[] = [];
  if (list.length === 0) return out;

  const needConsent = list.filter((it) => isLevelDown(it) && !it.consentAttached);
  if (needConsent.length > 0) {
    out.push({
      id: 'consent',
      tone: 'block',
      title: `${needConsent.length} รายการยังไม่มีหนังสือยินยอม`,
      detail: `${needConsent.map((it) => it.person.name).join(' · ')} — ${CONSENT_RULE}`,
      source: 'ตรวจจากกฎ',
    });
  }

  const noReason = list.filter((it) => !it.reason || it.reason.clauses.length === 0);
  if (noReason.length > 0) {
    out.push({
      id: 'reason',
      tone: 'warn',
      title: `${noReason.length} รายการยังไม่ระบุเหตุผล`,
      detail:
        `${noReason.map((it) => it.person.name).join(' · ')} — ` +
        'ออกร่างได้โดยเว้นไว้ แต่ในเอกสารจะพิมพ์ว่า “ยังไม่ได้ระบุเหตุผล” ตรงช่องนั้น ไม่ใช่เว้นบรรทัดว่าง',
      source: 'คำนวณ',
    });
  }

  const losing = summarise(list).unitDelta.filter((u) => u.delta <= -2);
  if (losing.length > 0) {
    out.push({
      id: 'unit-loss',
      tone: 'warn',
      title: `${losing.length} หน่วยเสียคนสุทธิตั้งแต่ 2 คน`,
      detail: losing.map((u) => `${leafUnit(u.unit)} ${u.delta}`).join(' · '),
      source: 'คำนวณ',
    });
  }

  /* เกณฑ์ระยะเวลาที่ใช้ไม่เท่ากันภายในชุดเดียวกัน คือเรื่องที่ผู้ร้องเรียนจับได้ก่อนเพื่อน */
  const months = list.map((it) => it.person.monthsInPosition).filter((m): m is number => m !== null);
  if (months.length >= 2) {
    const long = months.filter((m) => m >= tenureMonths).length;
    const short = list.filter(
      (it) => it.person.monthsInPosition !== null && it.person.monthsInPosition < tenureMonths / 2,
    );
    if (long > 0 && short.length > 0) {
      out.push({
        id: 'tenure-mix',
        tone: 'note',
        title: 'ใช้เกณฑ์ระยะเวลาไม่เท่ากันในชุดเดียวกัน',
        detail:
          `${long} รายการครองตำแหน่งเดิมเกินเกณฑ์หมุนเวียนแล้ว แต่ ` +
          `${short.map((it) => `${it.person.name} (${durationTH(it.person.monthsInPosition)})`).join(' · ')} ` +
          'สั้นกว่าครึ่งเกณฑ์ หากใช้เหตุผลชุดเดียวกันควรมีข้ออื่นรองรับ',
        source: 'คำนวณ',
      });
    }
  }

  /* ⚠ ประเภทบริหารไม่ได้อยู่ในอำนาจปลัดตามมาตรา 57 เหมือนอีกสามประเภท
     ถ้าปนอยู่ในคำสั่งเดียวกัน เรื่องทั้งชุดจะเดินช้าตามรายการที่ช้าที่สุด */
  const exec = list.filter((it) => it.target.level.startsWith('บริหาร'));
  if (exec.length > 0 && exec.length < list.length) {
    out.push({
      id: 'authority',
      tone: 'note',
      title: 'ผู้มีอำนาจสั่งบรรจุไม่ใช่คนเดียวกันทั้งชุด',
      detail:
        `${exec.length} รายการเป็นตำแหน่งประเภทบริหาร ซึ่งอยู่นอกอำนาจปลัดกระทรวงตามมาตรา 57 ` +
        `ส่วนอีก ${list.length - exec.length} รายการอยู่ในอำนาจปลัด — แยกเป็นคนละคำสั่งจะเดินเรื่องได้เร็วกว่า`,
      source: 'ตรวจจากกฎ',
    });
  }

  return out;
}

export function leafUnit(unit: string): string {
  const parts = unit.split(' > ');
  return parts[parts.length - 1] ?? unit;
}

export function durationTH(months: number | null): string {
  if (months === null) return 'ไม่ทราบระยะเวลา';
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (!y) return `${m} เดือน`;
  return m ? `${y} ปี ${m} เดือน` : `${y} ปี`;
}

/**
 * แปลงร่างเป็นสิ่งที่ส่งให้ผู้ช่วยอ่าน
 *
 * ⚠ ไม่ส่ง personaId ไปด้วยโดยตั้งใจ — ผู้ช่วยตัวนี้อ่านถ้อยคำอย่างเดียว
 *   ไม่ต้องเปิดข้อมูลบุคคลเพิ่ม จึงไม่มีช่องให้มันดึงคนนอกขอบเขตออกมา
 */
/**
 * ⚠ กรอง proposed **ข้างในนี้** ไม่ใช่ให้ผู้เรียกกรองมา
 *   ผู้ที่อยู่ในบัญชีพิจารณาแต่ยังไม่ถูกเสนอ ห้ามนับเข้ายอดใด ๆ ทั้งสิ้น
 *   ถ้าย้ายการกรองออกไปอยู่ที่ผู้เรียก วันหนึ่งจะมีคนเรียกโดยลืมกรอง แล้วตัวเลขผิดเงียบ ๆ
 */
export function toReviewItems(all: DraftItem[]): RotationReviewItem[] {
  return all
    .filter((it) => it.status === 'proposed')
    .map((it, i) => ({
      no: i + 1,
      name: it.person.name,
      fromTitle: it.person.title,
      fromUnit: leafUnit(it.person.unit),
      fromMonths: it.person.monthsInPosition,
      toTitle: it.target.title,
      toUnit: leafUnit(it.target.unit),
      toPosNo: it.target.posNo,
      vacantMonths: it.target.vacantMonths,
      clauses: it.reason?.clauses ?? [],
      reason: it.reason?.text ?? '',
      evidence: it.reason?.evidence ?? null,
      levelDown: isLevelDown(it),
      consent: it.consentAttached,
    }));
}
